import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import LoadingProgress from 'common/components/progress/loading';
import SecondaryButton from 'common/components/buttons/secondaryButton';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getJourneyReport } from './journeysActions';

const CARD_STYLE = {
  background: '#ffffff',
  borderRadius: 8,
  padding: 20,
  height: '100%',
};

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) {
    return '-';
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)} min`;
  }
  if (seconds < 86400) {
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}min`;
  }
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
}

function formatNumber(value) {
  if (value === null || value === undefined) {
    return '-';
  }
  return Number.isInteger(value) ? value : value.toFixed(2).replace('.', ',');
}

function Metric({ label, value, hint }) {
  return (
    <Box style={CARD_STYLE}>
      <Typography variant="subtitle2" style={{ color: '#8A8A8A' }}>
        {label}
      </Typography>
      <Typography variant="h4" style={{ fontWeight: 500 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" style={{ color: '#8A8A8A' }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

Metric.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  hint: PropTypes.string,
};

function Section({ title, description, children }) {
  return (
    <GeneralGrid item xs={12}>
      <Box style={{ ...CARD_STYLE, marginTop: 24 }}>
        <Typography variant="h6">{title}</Typography>
        {description ? (
          <Typography variant="body2" style={{ color: '#8A8A8A', marginBottom: 12 }}>
            {description}
          </Typography>
        ) : null}
        {children}
      </Box>
    </GeneralGrid>
  );
}

Section.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.any,
};

function JourneyReport(props) {
  const { journeys, router } = props;
  const report = journeys.report;
  const journeyId = router.params.id;
  const backTo =
    props.location?.query?.from === 'rank'
      ? `/owner/journeys?tab=rank&journey=${journeyId}`
      : '/owner/journeys';

  useEffect(() => {
    props.getJourneyReport(journeyId);
    // eslint-disable-next-line
  }, [journeyId]);

  if (journeys.reportLoading || !report) {
    return (
      <Box display="flex" height="100vh" justifyContent="center" mt={7}>
        <LoadingProgress />
      </Box>
    );
  }

  const { conclusion, funnel, activities, quizzes, ranking_distribution, rewards } = report;
  const maxFunnel = Math.max(...funnel.map((level) => level.users_completed), 1);

  return (
    <section style={{ padding: '0 2em 4em 2em' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        style={{ padding: '2em 0' }}>
        <Box>
          <Typography variant="h5">Relatório · {report.journey.name}</Typography>
          <Typography variant="body2" style={{ color: '#8A8A8A' }}>
            {report.journey.total_actions} atividades em {report.journey.total_levels} níveis
          </Typography>
        </Box>
        <SecondaryButton onClick={() => router.push(backTo)}>Voltar</SecondaryButton>
      </Box>

      <GeneralGrid container spacing={3}>
        <GeneralGrid item xs={12} md={3}>
          <Metric
            label="Começaram"
            value={conclusion.started}
            hint="fizeram ao menos uma atividade"
          />
        </GeneralGrid>
        <GeneralGrid item xs={12} md={3}>
          <Metric
            label="Concluíram"
            value={conclusion.completed}
            hint="fizeram todas as atividades"
          />
        </GeneralGrid>
        <GeneralGrid item xs={12} md={3}>
          <Metric
            label="Taxa de conclusão"
            value={`${conclusion.completion_rate}%`}
            hint="concluíram / começaram"
          />
        </GeneralGrid>
        <GeneralGrid item xs={12} md={3}>
          <Metric
            label="Tempo médio"
            value={formatDuration(conclusion.avg_completion_seconds)}
            hint="da primeira à última atividade"
          />
        </GeneralGrid>

        <Section
          title="Funil por nível"
          description="Quantos participantes concluíram cada nível e todos os anteriores.">
          {funnel.map((level) => (
            <Box key={level.level_id} style={{ marginBottom: 16 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">
                  Nível {level.level}
                  {level.name ? ` · ${level.name}` : ''}
                </Typography>
                <Typography variant="body2" style={{ color: '#8A8A8A' }}>
                  {level.users_completed} participantes · {level.rate_of_started}% de quem começou
                  {level.level > 1 ? ` · ${level.rate_of_previous}% do nível anterior` : ''}
                </Typography>
              </Box>
              <Box style={{ background: '#EFEFEF', borderRadius: 4, height: 12, marginTop: 4 }}>
                <Box
                  style={{
                    width: `${(level.users_completed / maxFunnel) * 100}%`,
                    background: '#2D95F4',
                    borderRadius: 4,
                    height: 12,
                  }}
                />
              </Box>
              {level.drop_from_previous > 0 ? (
                <Typography variant="caption" style={{ color: '#D0455B' }}>
                  {level.drop_from_previous} participantes abandonaram neste nível
                </Typography>
              ) : null}
            </Box>
          ))}
        </Section>

        <Section
          title="Engajamento por atividade"
          description="Quantos foram alcançados pela atividade e quantos efetivamente participaram.">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Atividade</TableCell>
                <TableCell align="center">Nível</TableCell>
                <TableCell align="center">Alcance</TableCell>
                <TableCell align="center">Impressões</TableCell>
                <TableCell align="center">Participaram</TableCell>
                <TableCell align="center">Taxa</TableCell>
                <TableCell align="center">Tempo médio até aqui</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.map((activity) => (
                <TableRow key={activity.action_id}>
                  <TableCell>{activity.name}</TableCell>
                  <TableCell align="center">{activity.level || '-'}</TableCell>
                  <TableCell align="center">{activity.reached}</TableCell>
                  <TableCell align="center">{activity.impressions}</TableCell>
                  <TableCell align="center">{activity.participants}</TableCell>
                  <TableCell align="center">{activity.participation_rate}%</TableCell>
                  <TableCell align="center">
                    {formatDuration(activity.avg_time_from_start_seconds)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>

        <Section
          title="Desempenho nos quizzes"
          description="Percentual de acerto por pergunta. Percentuais muito baixos indicam pergunta difícil demais ou mal formulada.">
          {quizzes.length < 1 ? (
            <Typography variant="body2" style={{ color: '#8A8A8A' }}>
              Nenhuma atividade desta jornada tem perguntas com alternativa correta.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pergunta</TableCell>
                  <TableCell>Atividade</TableCell>
                  <TableCell align="center">Respostas</TableCell>
                  <TableCell align="center">Acertos</TableCell>
                  <TableCell align="center">% de acerto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quizzes.map((quiz) => (
                  <TableRow key={quiz.question_id}>
                    <TableCell>{quiz.question}</TableCell>
                    <TableCell>{quiz.action_name}</TableCell>
                    <TableCell align="center">{quiz.answers}</TableCell>
                    <TableCell align="center">{quiz.correct}</TableCell>
                    <TableCell align="center">
                      <span style={{ color: quiz.correct_rate < 50 ? '#D0455B' : undefined }}>
                        {quiz.correct_rate}%
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>

        <Section
          title="Ranking e comportamento competitivo"
          description="Distribuição dos participantes no ranking de pontos da jornada.">
          <GeneralGrid container spacing={3}>
            <GeneralGrid item xs={12} md={3}>
              <Metric label="No ranking" value={ranking_distribution.ranked} />
            </GeneralGrid>
            <GeneralGrid item xs={12} md={3}>
              <Metric label="Top 10%" value={ranking_distribution.top_10_percent} />
            </GeneralGrid>
            <GeneralGrid item xs={12} md={3}>
              <Metric
                label="Metade inferior"
                value={ranking_distribution.bottom}
                hint="abaixo da mediana"
              />
            </GeneralGrid>
            <GeneralGrid item xs={12} md={3}>
              <Metric
                label="Nunca entraram"
                value={ranking_distribution.viewers_never_participated}
                hint="viram alguma atividade e não participaram"
              />
            </GeneralGrid>
          </GeneralGrid>
        </Section>

        <Section
          title="Recompensas e custo"
          description="Pontos previstos pelo valor configurado nas atividades e o total efetivamente creditado no extrato dos participantes.">
          <GeneralGrid container spacing={3}>
            <GeneralGrid item xs={12} md={4}>
              <Metric label="Pontos previstos" value={formatNumber(rewards.points_configured)} />
            </GeneralGrid>
            <GeneralGrid item xs={12} md={4}>
              <Metric label="Pontos creditados" value={formatNumber(rewards.points_credited)} />
            </GeneralGrid>
            <GeneralGrid item xs={12} md={4}>
              <Metric
                label="Média por participante"
                value={formatNumber(rewards.avg_points_per_participant)}
              />
            </GeneralGrid>
          </GeneralGrid>
          <Table size="small" style={{ marginTop: 16 }}>
            <TableHead>
              <TableRow>
                <TableCell>Atividade</TableCell>
                <TableCell align="center">Pontos por participação</TableCell>
                <TableCell align="center">Participações</TableCell>
                <TableCell align="center">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rewards.by_action.map((item) => (
                <TableRow key={item.action_id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="center">{formatNumber(item.points)}</TableCell>
                  <TableCell align="center">{item.participants}</TableCell>
                  <TableCell align="center">{formatNumber(item.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      </GeneralGrid>
    </section>
  );
}

JourneyReport.propTypes = {
  getJourneyReport: PropTypes.func,
  location: PropTypes.shape({
    query: PropTypes.shape({ from: PropTypes.string }),
  }),
  journeys: PropTypes.shape({
    report: PropTypes.any,
    reportLoading: PropTypes.any,
  }),
  router: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.any }),
    push: PropTypes.func,
  }),
};

const mapStateToProps = (state) => {
  return { journeys: state.journeys, auth: state.auth };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({ getJourneyReport }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(JourneyReport);
